import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { KKuTuIOApi } from '../../KKuTuIOApi';

export interface AdminProfile {
  id: string;
  name: string;
  team: string;
  privileges: string[];
}

export interface SessionState {
  loggedIn: boolean;
  user: AdminProfile | Record<string, never>;
}

const initialState: SessionState = {
  loggedIn: false,
  user: {},
};

export function parseAdminProfile(value: unknown): AdminProfile | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.id !== 'string' || !candidate.id
    || typeof candidate.name !== 'string'
    || typeof candidate.team !== 'string'
    || !Array.isArray(candidate.privileges)
    || candidate.privileges.some(privilege => typeof privilege !== 'string')) return null;
  return {
    id: candidate.id,
    name: candidate.name,
    team: candidate.team,
    privileges: [...new Set(candidate.privileges)],
  };
}

export const fetchUser = createAsyncThunk<AdminProfile | null>('session/fetchProfile', async () => {
  try {
    const { data } = await KKuTuIOApi.get<unknown>('/profile', { timeout: 8_000 });
    return parseAdminProfile(data);
  } catch {
    return null;
  }
});

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AdminProfile>) {
      state.loggedIn = true;
      state.user = action.payload;
    },
    logout(state) {
      state.loggedIn = false;
      state.user = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.loggedIn = action.payload !== null;
      state.user = action.payload ?? {};
    });
    builder.addCase(fetchUser.rejected, state => {
      state.loggedIn = false;
      state.user = {};
    });
  },
});

export const { logout, setUser } = sessionSlice.actions;
export default sessionSlice.reducer;
